import PageHeader from '../../components/PageHeader';
// src/pages/company/GeofencingDashboard.tsx
// CHANGES v2:
//  1. Overview: Fleet Vehicles replaced with search-to-select autocomplete
//  2. Zones tab: compact paginated table instead of card list
//  3. All server-side pagination + debounced search preserved

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Chip, Alert, List,
  ListItem, ListItemText, IconButton, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  FormControlLabel, Checkbox, Autocomplete, useTheme, useMediaQuery,
  Stack, Divider, InputAdornment, Paper, TablePagination,
  Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel,
  CircularProgress, Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon, CheckCircle as CheckCircleIcon,
  Edit as EditIcon, Delete as DeleteIcon, FileDownload as DownloadIcon,
  ContentCopy as CopyIcon, Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon, Add as AddIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';

import api from '../../services/api';
import GeofenceZoneDialog from '../../components/GeofenceZoneDialog';
import BulkZoneDialog from '../../components/BulkZoneDialog';
import ZoneTemplatesDialog from '../../components/ZoneTemplatesDialog';
import GeofenceAnalyticsPanel from '../../components/GeofenceAnalyticsPanel';
import ViolationPredictionWidget from '../../components/ViolationPredictionWidget';
import { formatDateTime } from '../../utils/dateUtils';


async function downloadViolationFeed(format: 'csv' | 'json', filters: Record<string, any>) {
  const records: any[] = [];
  for (let skip = 0; ; skip += 200) {
    const response = await api.get('/geofencing/violations', { params: { ...filters, includeSpeed: true, limit: 200, skip } });
    const batch = response.data.data || [];
    records.push(...batch);
    if (!batch.length || records.length >= response.data.pagination.total) break;
  }
  const cell = (value: any) => {
    let text = String(value ?? '');
    if (/^[=+@-]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  const csv = [
    ['Plate', 'Type', 'Event time', 'Zone', 'Speed km/h', 'Limit km/h', 'Source', 'Review status'],
    ...records.map(v => [v.plateNumber, v.violationType, v.timestamp, v.zoneName, v.currentSpeed ?? v.speed,
      v.speedLimit, v.source || 'Saved alert', v.canAcknowledge === false ? 'Telemetry record' : v.acknowledgedBy ? 'Acknowledged' : 'Pending'])
  ].map(row => row.map(cell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([format === 'csv' ? csv : JSON.stringify(records, null, 2)],
    { type: format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json' }));
  const link = document.createElement('a');
  link.href = url; link.download = `fleet-violations-${Date.now()}.${format}`; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface GeofenceZone {
  _id: string;
  name: string;
  type: 'circular' | 'polygon';
  isActive: boolean;
  rules: {
    alertOnEntry: boolean;
    alertOnExit: boolean;
    alertOnBreach: boolean;
    speedLimit?: number;
  };
  activeSchedule?: { enabled: boolean; days: number[]; startTime: string; endTime: string };
  overlapsDetected?: any[];
}

interface VehicleGeofenceStatus {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  geofencingEnabled: boolean;
  zoneCount: number;
  currentStatus?: { isInside: boolean; activeZones: Array<{ zoneName: string }> };
}

// ─── Vehicle Search Autocomplete (for Overview) ───────────────────────────────

interface VehicleSearchProps {
  value: VehicleGeofenceStatus | null;
  onChange: (v: VehicleGeofenceStatus | null) => void;
}

const VehicleSearch: React.FC<VehicleSearchProps> = ({ value, onChange }) => {
  const [options,  setOptions]  = useState<VehicleGeofenceStatus[]>([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const debounce = useRef<NodeJS.Timeout | null>(null);

  const search = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const res = await api.get('/geofencing/vehicles', {
        params: { page: 1, limit: 50, search: q || undefined },
      });
      setOptions(res.data.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { search(''); }, [search]);

  const handleInputChange = (_: any, val: string) => {
    setInput(val);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(val), 350);
  };

  return (
    <Autocomplete
      options={options}
      value={value}
      loading={loading}
      inputValue={input}
      onInputChange={handleInputChange}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={v => v.plateNumber}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      filterOptions={x => x} // server-side filtering
      renderOption={(props, v) => (
        <Box component="li" {...props} key={v._id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <CarIcon fontSize="small" color="action" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>{v.plateNumber}</Typography>
              <Typography variant="caption" color="text.secondary">{v.make} {v.model}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Chip size="small" label={`${v.zoneCount} zones`}
                color={v.zoneCount > 0 ? 'primary' : 'default'} sx={{ height: 20, fontSize: '0.68rem' }} />
              {v.geofencingEnabled
                ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                : <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
            </Box>
          </Box>
        </Box>
      )}
      renderInput={params => (
        <TextField {...params} placeholder="Search plate, make, model…" size="small" fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  {loading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

// ─── Selected Vehicle Summary Card ────────────────────────────────────────────

const VehicleSummaryCard: React.FC<{
  vehicle: VehicleGeofenceStatus;
  onGoToZones: () => void;
}> = ({ vehicle, onGoToZones }) => (
  <Card variant="outlined" sx={{ mt: 1.5, borderColor: vehicle.geofencingEnabled ? 'success.main' : 'warning.main' }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h6" fontWeight={700}>{vehicle.plateNumber}</Typography>
          <Typography variant="body2" color="text.secondary">{vehicle.make} {vehicle.model}</Typography>
        </Box>
        {vehicle.geofencingEnabled
          ? <Chip size="small" icon={<CheckCircleIcon />} label="Geofencing ON" color="success" />
          : <Chip size="small" icon={<WarningIcon />}     label="Geofencing OFF" color="warning" />}
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap">
        <Box>
          <Typography variant="caption" color="text.secondary">Zones</Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">{vehicle.zoneCount}</Typography>
        </Box>
        {vehicle.currentStatus?.isInside && (
          <Box>
            <Typography variant="caption" color="text.secondary">Currently</Typography>
            <Typography variant="body2" fontWeight={600} color="success.main">Inside Zone</Typography>
          </Box>
        )}
        {vehicle.currentStatus?.activeZones?.length ? (
          <Box>
            <Typography variant="caption" color="text.secondary">Active zones</Typography>
            <Typography variant="body2">{vehicle.currentStatus.activeZones.map(z => z.zoneName).join(', ')}</Typography>
          </Box>
        ) : null}
      </Stack>
      <Button size="small" variant="outlined" sx={{ mt: 1.5, textTransform: 'none' }}
        onClick={onGoToZones}>
        Manage Zones →
      </Button>
    </CardContent>
  </Card>
);

// ─── Violations Tab ───────────────────────────────────────────────────────────

const ViolationsTab: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const [violations,   setViolations]   = useState<any[]>([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [searchInput,  setSearchInput]  = useState('');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'acknowledged'>('all');
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [days, setDays] = useState(7);
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(20);
  const [bulkSelected, setBulkSelected] = useState<string[]>([]);
  const [bulkLoading,  setBulkLoading]  = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const requestSequence = useRef(0);

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { setSearchQuery(value); setPage(0); }, 400);
  };

  const fetchViolations = useCallback(async () => {
    const request = ++requestSequence.current;
    setLoading(true);
    try {
      const now = new Date();
      const params: any = { includeSpeed: true, startDate: new Date(now.getTime() - days * 86400000).toISOString(), endDate: now.toISOString(), limit: rowsPerPage, skip: page * rowsPerPage };
      if (searchQuery)            params.search        = searchQuery;
      if (statusFilter !== 'all') params.acknowledged  = statusFilter === 'acknowledged' ? 'true' : 'false';
      if (typeFilter   !== 'all') params.violationType = typeFilter;
      const res = await api.get('/geofencing/violations', { params });
      if (request !== requestSequence.current) return;
      setError('');
      setBulkSelected([]);
      setViolations(res.data.data || []);
      setTotal(res.data.pagination?.total ?? res.data.data?.length ?? 0);
    } catch (err) {
      console.error('Error fetching violations:', err);
      if (request === requestSequence.current) setError('Unable to load violations. Please try again.');
    } finally {
      if (request === requestSequence.current) setLoading(false);
    }
  }, [searchQuery, statusFilter, typeFilter, page, rowsPerPage, days]);

  useEffect(() => { fetchViolations(); }, [fetchViolations]);
  useEffect(() => { setPage(0); setBulkSelected([]); }, [searchQuery, statusFilter, typeFilter, days]);

  const acknowledge = async (id: string) => {
    try {
      await api.put(`/geofencing/violations/${id}/acknowledge`, {});
      fetchViolations();
    } catch { setError('Unable to acknowledge this alert. Please try again.'); }
  };

  const bulkAcknowledge = async () => {
    if (!bulkSelected.length) return;
    setBulkLoading(true);
    try {
      await api.post('/geofencing/violations/bulk-acknowledge', { violationIds: bulkSelected });
      setBulkSelected([]);
      fetchViolations();
    } catch { setError('Unable to acknowledge alerts. Please try again.'); } finally { setBulkLoading(false); }
  };

  const toggleSelect    = (id: string) =>
    setBulkSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const selectable = violations.filter(v => v.canAcknowledge !== false && !v.acknowledgedBy);
  const toggleSelectAll = () =>
    setBulkSelected(p => p.length === selectable.length ? [] : selectable.map(v => v._id));

  const TYPES = ['all','unauthorized_entry','unauthorized_exit','breach','speed_violation','zone_speed_violation','global_speed_violation'];

  const theme   = useTheme();
  const isDark  = theme.palette.mode === 'dark';
  const border  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const surface = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const allSel  = selectable.length > 0 && bulkSelected.length === selectable.length;

  return (
    <Box>
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between"
        alignItems={isMobile ? 'stretch' : 'center'} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Speed & Geofence Violations</Typography>
          <Typography variant="body2" color="text.secondary">
            {total.toLocaleString()} in the last {days} days{bulkSelected.length > 0 && ` · ${bulkSelected.length} selected`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {bulkSelected.length > 0 && (
            <Button size="small" variant="contained" color="success"
              onClick={bulkAcknowledge} disabled={bulkLoading}>
              {bulkLoading ? 'Processing…' : `Acknowledge ${bulkSelected.length}`}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
            disabled={exporting} onClick={async () => {
              setExporting(true);
              const now = new Date();
              try { await downloadViolationFeed('csv', { startDate: new Date(now.getTime() - days * 86400000).toISOString(),
                endDate: now.toISOString(), search: searchQuery, violationType: typeFilter,
                ...(statusFilter !== 'all' ? { acknowledged: statusFilter === 'acknowledged' ? 'true' : 'false' } : {}) }); }
              catch { setError('Unable to export violations. Please try again.'); }
              finally { setExporting(false); }
            }}>
            Export
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'}>
          <TextField value={searchInput} onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search plate or zone…" size="small" sx={{ minWidth: isMobile ? '100%' : 260 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: subtext }} /></InputAdornment>,
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchInput(''); handleSearchChange(''); }}>✕</IconButton>
                </InputAdornment>
              ) : undefined,
            }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {(['all','pending','acknowledged'] as const).map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '4px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase',
                  background: statusFilter === s ? '#f44336' : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                  color: statusFilter === s ? '#fff' : subtext }}>
                {s}
              </button>
            ))}
          </Stack>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select value={days} onChange={e => setDays(Number(e.target.value))} inputProps={{ 'aria-label': 'Event date range' }}>
              {[1, 7, 30, 90, 365].map(d => <MenuItem key={d} value={d}>Last {d === 1 ? '24 hours' : `${d} days`}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              {TYPES.map(t => <MenuItem key={t} value={t}>{t === 'all' ? 'All Types' : t.replace(/_/g,' ')}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Speed readings and trip peaks use the Speed Analytics threshold of 80 km/h. Trip peak timestamps mark the trip report. Pending and acknowledged filters apply to saved alerts.
      </Typography>
      {/* List */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
        {violations.length > 0 && (
          <Box sx={{ px: 2.5, py: 1, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox size="small" checked={allSel} indeterminate={bulkSelected.length > 0 && !allSel}
              onChange={toggleSelectAll} disabled={!selectable.length} />
            <Typography variant="caption" color="text.secondary">
              {allSel ? 'Deselect all' : `Select ${selectable.length} saved alerts on this page`}
            </Typography>
            {bulkSelected.length > 0 && (
              <Button size="small" variant="text" color="error" onClick={() => setBulkSelected([])}>Clear</Button>
            )}
          </Box>
        )}

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Loading…</Typography></Box>
        ) : error ? null : violations.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography fontWeight="bold">No violations found</Typography>
            <Typography variant="body2" color="text.secondary">Try adjusting your filters</Typography>
          </Box>
        ) : violations.map((v, i) => {
          const isSel    = bulkSelected.includes(v._id);
          const isPending = v.canAcknowledge !== false && !v.acknowledgedBy;
          return (
            <Box key={v._id} sx={{
              px: 2.5, py: 1.5,
              borderBottom: i < violations.length - 1 ? `1px solid ${border}` : 'none',
              bgcolor: isSel ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)') : 'transparent',
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}>
              <Checkbox size="small" checked={isSel} disabled={v.canAcknowledge === false || !!v.acknowledgedBy} onChange={() => toggleSelect(v._id)} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography fontWeight="bold" fontSize="0.85rem">{v.plateNumber}</Typography>
                  <Box sx={{ px: 1, py: 0.3, borderRadius: 1.5,
                    bgcolor: v.violationType?.includes('speed') ? 'rgba(245,158,11,0.12)' : isPending ? 'rgba(248,113,113,0.12)' : 'rgba(52,211,153,0.12)' }}>
                    <Typography fontSize="0.68rem" fontWeight={800}
                      color={v.violationType?.includes('speed') ? '#b45309' : isPending ? '#f87171' : '#34d399'} sx={{ textTransform: 'uppercase' }}>
                      {v.violationType?.replace(/_/g,' ')}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary">{v.zoneName ? <>Zone: <strong>{v.zoneName}</strong></> : v.source}</Typography>
                {(v.currentSpeed || v.speed) > 0 && <Typography variant="caption" color="text.secondary" display="block">Speed: {Math.round(v.currentSpeed ?? v.speed)} km/h{v.speedLimit ? ` · Limit: ${v.speedLimit} km/h` : ''}</Typography>}
                <Typography variant="caption" color="text.secondary" display="block">{formatDateTime(v.timestamp)}</Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                {v.canAcknowledge === false ? <Typography variant="caption" color="text.secondary">Telemetry record</Typography> : isPending
                  ? <Button size="small" variant="outlined" color="success" onClick={() => acknowledge(v._id)}
                      sx={{ fontSize: '0.72rem', textTransform: 'none', fontWeight: 700 }}>Acknowledge</Button>
                  : <Typography fontSize="0.72rem" color="text.secondary">✓ Acknowledged</Typography>}
              </Box>
            </Box>
          );
        })}

        <Divider sx={{ borderColor: border }} />
        <TablePagination component="div" count={total} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count.toLocaleString()}`} />
      </Paper>
    </Box>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const GeofencingDashboard: React.FC = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Vehicle state
  const [allVehicles,     setAllVehicles]     = useState<VehicleGeofenceStatus[]>([]);
  const [vehicleTotal,    setVehicleTotal]    = useState(0);
  const [vehiclePage,     setVehiclePage]     = useState(0);
  const [vehicleLimit]                        = useState(25);
  const [vehicleSearch,   setVehicleSearch]   = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleGeofenceStatus | null>(null);

  const [zones,       setZones]       = useState<GeofenceZone[]>([]);
  const [zoneSearch,  setZoneSearch]  = useState('');
  const [violations,  setViolations]  = useState<any[]>([]);
  const [statistics,  setStatistics]  = useState<any>(null);
  const [analytics,   setAnalytics]   = useState<any>(null);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState(0);
  const [predictions, setPredictions] = useState<any>(null);

  // Dialog states
  const [openZoneDialog,      setOpenZoneDialog]      = useState(false);
  const [openBulkDialog,      setOpenBulkDialog]      = useState(false);
  const [openTemplatesDialog, setOpenTemplatesDialog] = useState(false);
  const [openExportDialog,    setOpenExportDialog]    = useState(false);
  const [editingZone,         setEditingZone]         = useState<GeofenceZone | null>(null);

  // Zones tab vehicle table state
  const [zoneTabPage,  setZoneTabPage]  = useState(0);
  const [zoneTabLimit] = useState(25);
  const [zoneTabInput, setZoneTabInput] = useState('');
  const [zoneTabSearch,setZoneTabSearch]= useState('');
  const [zoneTabVehicles, setZoneTabVehicles] = useState<VehicleGeofenceStatus[]>([]);
  const [zoneTabTotal,    setZoneTabTotal]    = useState(0);
  const [zoneTabLoading,  setZoneTabLoading]  = useState(false);
  const [sortField, setSortField] = useState<'plateNumber'|'zoneCount'|'geofencingEnabled'>('plateNumber');
  const [sortDir,   setSortDir]   = useState<'asc'|'desc'>('asc');

  const zoneTabDebounce = useRef<NodeJS.Timeout | null>(null);
  const handleZoneTabSearch = (val: string) => {
    setZoneTabInput(val);
    if (zoneTabDebounce.current) clearTimeout(zoneTabDebounce.current);
    zoneTabDebounce.current = setTimeout(() => { setZoneTabSearch(val); setZoneTabPage(0); }, 400);
  };

  // Fetch vehicles for Zones tab table
  const fetchZoneTabVehicles = useCallback(async () => {
    setZoneTabLoading(true);
    try {
      const res = await api.get('/geofencing/vehicles', {
        params: { page: zoneTabPage + 1, limit: zoneTabLimit, search: zoneTabSearch || undefined },
      });
      setZoneTabVehicles(res.data.data || []);
      setZoneTabTotal(res.data.pagination?.total ?? 0);
    } catch { /* silent */ }
    finally { setZoneTabLoading(false); }
  }, [zoneTabPage, zoneTabLimit, zoneTabSearch]);

  useEffect(() => {
    if (activeTab === 1) fetchZoneTabVehicles();
  }, [activeTab, fetchZoneTabVehicles]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Sort zone tab vehicles client-side (already paginated from server)
  const sortedZoneVehicles = [...zoneTabVehicles].sort((a, b) => {
    let va: any = a[sortField];
    let vb: any = b[sortField];
    if (typeof va === 'boolean') va = va ? 1 : 0;
    if (typeof vb === 'boolean') vb = vb ? 1 : 0;
    if (typeof va === 'string')  va = va.toLowerCase();
    if (typeof vb === 'string')  vb = vb.toLowerCase();
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  useEffect(() => {
    fetchStatistics();
    fetchRecentViolations();
    fetchAnalytics();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedVehicle) {
      fetchZones(selectedVehicle._id);
      checkPredictions(selectedVehicle._id);
    }
  }, [selectedVehicle]);

  const fetchZones = async (vehicleId: string) => {
    try {
      const res = await api.get(`/geofencing/vehicles/${vehicleId}/zones`);
      setZones(res.data.data?.zones || res.data.data || []);
    } catch (e) { console.error('fetchZones:', e); }
  };

  const fetchRecentViolations = async () => {
    try {
      const res = await api.get('/geofencing/violations', { params: { includeSpeed: true, limit: 5, startDate: new Date(Date.now() - 86400000).toISOString(), endDate: new Date().toISOString() } });
      setViolations(res.data.data || []);
    } catch (e) { console.error('fetchRecentViolations:', e); }
  };

  const fetchStatistics = async () => {
    try {
      const res = await api.get('/geofencing/statistics');
      setStatistics(res.data.data);
    } catch (e) { console.error('fetchStatistics:', e); }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/geofencing/analytics');
      setAnalytics(res.data.data);
    } catch (e) { console.error('fetchAnalytics:', e); }
  };

  const checkPredictions = async (vehicleId: string) => {
    try {
      const res = await api.get(`/geofencing/vehicles/${vehicleId}/predict`);
      setPredictions(res.data.prediction);
    } catch { /* silent */ }
  };

  const deleteZone = async (zoneId: string) => {
    if (!selectedVehicle || !zoneId || zoneId === 'undefined') return;
    if (!window.confirm('Delete this zone?')) return;
    try {
      await api.delete(`/geofencing/vehicles/${selectedVehicle._id}/zones/${zoneId}`);
      fetchZones(selectedVehicle._id);
      fetchStatistics();
    } catch (e: any) {
      alert(`Failed to delete: ${e.response?.data?.message || e.message}`);
    }
  };

  const duplicateZone = async (zone: GeofenceZone) => {
    if (!selectedVehicle) return;
    try {
      await api.post(`/geofencing/vehicles/${selectedVehicle._id}/zones`, {
        ...zone, name: `${zone.name} (Copy)`, _id: undefined,
      });
      fetchZones(selectedVehicle._id);
    } catch (e) { console.error('duplicateZone:', e); }
  };

  const acknowledgeViolation = async (vehicleId: string, violationId: string) => {
    try {
      await api.put(`/geofencing/violations/${violationId}/acknowledge`, {});
      fetchRecentViolations();
    } catch (e) { console.error('acknowledgeViolation:', e); }
  };

  const exportViolations = async (format: 'csv' | 'json') => {
    const now = new Date();
    await downloadViolationFeed(format, { startDate: new Date(now.getTime() - 7 * 86400000).toISOString(), endDate: now.toISOString() });
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 0, width: '100%', boxSizing: 'border-box', overflowX: 'hidden', bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <PageHeader title="Geofencing" description="Define zones, monitor entry and exit alerts, and review violations" icon="geofencing" actions={<>
{isMobile ? (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {[['Templates', () => setOpenTemplatesDialog(true)],
              ['Bulk',      () => setOpenBulkDialog(true)],
              ['Export',    () => setOpenExportDialog(true)]].map(([label, fn]: any) => (
              <Button key={label} variant="outlined" size="small" onClick={fn}
                sx={{ minWidth: 'fit-content', px: 1.5, whiteSpace: 'nowrap' }}>{label}</Button>
            ))}
          </Box>
        ) : (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<AssessmentIcon />} onClick={() => setOpenTemplatesDialog(true)}>Templates</Button>
            <Button variant="outlined" startIcon={<CopyIcon />}       onClick={() => setOpenBulkDialog(true)}>Bulk Manage</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />}   onClick={() => setOpenExportDialog(true)}>Export</Button>
          </Stack>
        )}
</>} />

      {/* ── Tabs ── */}
      <Box sx={{ width: '100%', overflowX: 'hidden', mb: 1.5 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false} allowScrollButtonsMobile
          sx={{ '& .MuiTab-root': { minWidth: isMobile ? 70 : 'auto', px: isMobile ? 0.5 : 2, fontSize: isMobile ? '0.75rem' : '0.875rem', textTransform: 'none' } }}>
          <Tab label="Overview" />
          <Tab label="Zones" />
          <Tab label="Violations" />
          <Tab label="Analytics" icon={<AnalyticsIcon sx={{ fontSize: isMobile ? 16 : 20 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════════
          Tab 0: Overview
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 0 && (
        <>
          {/* Stat cards */}
          {statistics && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {[
                { label: isMobile ? 'Vehicles' : 'Total Vehicles', value: statistics.totalVehicles,
                  sub: `${statistics.vehiclesWithGeofencing} with geofencing`, color: 'text.primary' },
                { label: isMobile ? 'Zones' : 'Active Zones', value: statistics.activeZones,
                  sub: `${statistics.totalZones} total`, color: 'primary.main' },
                { label: 'Saved violations', value: statistics.violations.total,
                  sub: `All time · ${statistics.violations.unacknowledged} pending`, color: 'error.main' },
                { label: 'Compliance', value: `${statistics.violations.total > 0
                    ? Math.round((1 - statistics.violations.unacknowledged / statistics.violations.total) * 100) : 100}%`,
                  sub: '', color: 'success.main' },
              ].map(({ label, value, sub, color }) => (
                <Grid item xs={6} md={3} key={label}>
                  <Card sx={{ bgcolor: 'background.paper' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="textSecondary" display="block">{label}</Typography>
                      <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" sx={{ color }}>{value}</Typography>
                      {sub && <Typography variant="caption" color="textSecondary">{sub}</Typography>}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {!isMobile && selectedVehicle && predictions?.hasPrediction && (
            <ViolationPredictionWidget prediction={predictions} vehicle={selectedVehicle} />
          )}

          <Grid container spacing={isMobile ? 1.5 : 3}>
            {/* ── Vehicle selector (search-to-select) ── */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="subtitle1" fontWeight="bold">Fleet Vehicle</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {statistics?.totalVehicles?.toLocaleString() ?? '–'} vehicles
                    </Typography>
                  </Box>
                  <VehicleSearch value={selectedVehicle} onChange={setSelectedVehicle} />
                  {selectedVehicle && (
                    <VehicleSummaryCard
                      vehicle={selectedVehicle}
                      onGoToZones={() => setActiveTab(1)}
                    />
                  )}
                  {!selectedVehicle && (
                    <Box sx={{ mt: 3, py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                      <CarIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Search and select a vehicle to view its geofencing status
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* ── Recent violations ── */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Recent Violations · Last 24 hours</Typography>
                    {violations.length > 0 && (
                      <Button size="small" variant="text" onClick={() => setActiveTab(2)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}>View all →</Button>
                    )}
                  </Box>
                  <List sx={{ p: 0 }}>
                    {violations.length === 0 ? (
                      <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', py: 2 }}>
                        No speed or geofence violations in the last 24 hours
                      </Typography>
                    ) : violations.map((v) => (
                      <ListItem key={v._id} sx={{ border: 1, borderColor: 'error.light', borderRadius: 1, mb: 1, p: isMobile ? 1 : 1.5 }}>
                        <ListItemText
                          primary={
                            <Stack spacing={0.5}>
                              <Typography variant={isMobile ? 'caption' : 'body2'} fontWeight="bold">{v.plateNumber}</Typography>
                              <Chip size="small" label={v.violationType.replace(/_/g,' ')} color="error"
                                sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem', width: 'fit-content' }} />
                            </Stack>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" display="block">{v.zoneName ? `Zone: ${v.zoneName}` : v.source}</Typography>
                              {(v.currentSpeed || v.speed) > 0 && <Typography variant="caption" display="block">{Math.round(v.currentSpeed ?? v.speed)} km/h{v.speedLimit ? ` · Limit: ${v.speedLimit} km/h` : ''}</Typography>}
                              <Typography variant="caption" display="block" color="textSecondary">{formatDateTime(v.timestamp)}</Typography>
                              {v.canAcknowledge !== false && !v.acknowledgedBy && <Button size="small" onClick={() => acknowledgeViolation(v.vehicleId, v._id)}
                                sx={{ mt: 0.5, fontSize: isMobile ? '0.7rem' : '0.8125rem' }} fullWidth={isMobile}>
                                Acknowledge
                              </Button>}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Tab 1: Zones — compact vehicle table + zone panel
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 1 && (
        <Grid container spacing={2}>
          {/* Left: vehicle table */}
          <Grid item xs={12} md={selectedVehicle ? 5 : 12}>
            <Card>
              <CardContent sx={{ p: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle1" fontWeight="bold">Select Vehicle</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {zoneTabTotal.toLocaleString()} vehicles
                  </Typography>
                </Box>
                <TextField size="small" fullWidth placeholder="Search plate, make, model…"
                  value={zoneTabInput} onChange={e => handleZoneTabSearch(e.target.value)} sx={{ mb: 1.5 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

                {zoneTabLoading ? (
                  <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ width: '100%', overflowX: 'auto' }}><Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel active={sortField === 'plateNumber'} direction={sortDir}
                              onClick={() => handleSort('plateNumber')}>Plate</TableSortLabel>
                          </TableCell>
                          <TableCell>Make / Model</TableCell>
                          <TableCell>
                            <TableSortLabel active={sortField === 'geofencingEnabled'} direction={sortDir}
                              onClick={() => handleSort('geofencingEnabled')}>Status</TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel active={sortField === 'zoneCount'} direction={sortDir}
                              onClick={() => handleSort('zoneCount')}>Zones</TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedZoneVehicles.map(v => (
                          <TableRow key={v._id} hover selected={selectedVehicle?._id === v._id}
                            onClick={() => setSelectedVehicle(v)} sx={{ cursor: 'pointer' }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{v.plateNumber}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">{v.make} {v.model}</Typography>
                            </TableCell>
                            <TableCell>
                              {v.geofencingEnabled
                                ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                : <WarningIcon    sx={{ fontSize: 18, color: 'warning.main' }} />}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={v.zoneCount}
                                color={v.zoneCount > 0 ? 'primary' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />
                            </TableCell>
                          </TableRow>
                        ))}
                        {sortedZoneVehicles.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="caption" color="text.secondary">No vehicles found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table></Box>
                  </Box>
                )}

                <TablePagination component="div" count={zoneTabTotal} page={zoneTabPage}
                  onPageChange={(_, p) => setZoneTabPage(p)} rowsPerPage={zoneTabLimit}
                  rowsPerPageOptions={[zoneTabLimit]}
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`} />
              </CardContent>
            </Card>
          </Grid>

          {/* Right: zones for selected vehicle */}
          {selectedVehicle && (
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent sx={{ p: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Zones — {selectedVehicle.plateNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedVehicle.make} {selectedVehicle.model}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={selectedVehicle.geofencingEnabled} color="success"
                            onChange={async (e) => {
                              try {
                                await api.put(`/geofencing/vehicles/${selectedVehicle._id}/enable`, { enabled: e.target.checked });
                                setSelectedVehicle({ ...selectedVehicle, geofencingEnabled: e.target.checked });
                                fetchZoneTabVehicles();
                              } catch (err: any) {
                                alert(`Failed: ${err.response?.data?.message || err.message}`);
                              }
                            }} />
                        }
                        label={<Typography variant="caption">{selectedVehicle.geofencingEnabled ? 'ON' : 'OFF'}</Typography>}
                        sx={{ mr: 0 }}
                      />
                      <Button size="small" variant="contained" startIcon={<AddIcon />}
                        onClick={() => { setEditingZone(null); setOpenZoneDialog(true); }}>
                        Add Zone
                      </Button>
                    </Stack>
                  </Box>

                  <TextField size="small" fullWidth placeholder="Search zones…" value={zoneSearch}
                    onChange={e => setZoneSearch(e.target.value)} sx={{ mb: 1.5 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

                  <List sx={{ p: 0 }}>
                    {zones
                      .filter(z => !zoneSearch || z.name.toLowerCase().includes(zoneSearch.toLowerCase()) || z.type.toLowerCase().includes(zoneSearch.toLowerCase()))
                      .map((zone) => (
                        <ListItem key={zone._id} sx={{
                          border: 1, borderColor: zone.isActive ? 'success.light' : 'divider',
                          borderRadius: 1, mb: 1, p: 1.5,
                          flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
                        }}>
                          <ListItemText
                            primary={
                              <Stack spacing={0.5}>
                                <Typography variant="body2" fontWeight="bold">{zone.name}</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Chip size="small" label={zone.type} sx={{ height: 20 }} />
                                  <Chip size="small" label={zone.isActive ? 'Active' : 'Inactive'}
                                    color={zone.isActive ? 'success' : 'default'} sx={{ height: 20 }} />
                                  {zone.activeSchedule?.enabled && <Chip size="small" label="Scheduled" color="info" sx={{ height: 20 }} />}
                                  {zone.rules.speedLimit && <Chip size="small" label={`${zone.rules.speedLimit} km/h`} sx={{ height: 20 }} />}
                                </Box>
                              </Stack>
                            }
                          />
                          <Box sx={{ display: 'flex', gap: 0.5, mt: isMobile ? 1 : 0 }}>
                            <Tooltip title="Duplicate">
                              <IconButton size="small" onClick={() => duplicateZone(zone)}><CopyIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => { setEditingZone(zone); setOpenZoneDialog(true); }}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => deleteZone(zone._id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </Box>
                        </ListItem>
                      ))}
                    {zones.length === 0 && (
                      <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                          No zones configured. Click "Add Zone" to create one.
                        </Typography>
                      </Box>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Tab 2: Violations
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 2 && <ViolationsTab isMobile={isMobile} />}

      {/* ══════════════════════════════════════════════════════════════════════
          Tab 3: Analytics
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 3 && analytics && (
        <GeofenceAnalyticsPanel analytics={analytics} onRefresh={fetchAnalytics} />
      )}

      {/* ── Dialogs ── */}
      <GeofenceZoneDialog
        open={openZoneDialog}
        onClose={() => { setOpenZoneDialog(false); setEditingZone(null); }}
        vehicleId={selectedVehicle?._id}
        zoneData={editingZone}
        onSuccess={() => {
          setOpenZoneDialog(false); setEditingZone(null);
          if (selectedVehicle) fetchZones(selectedVehicle._id);
          fetchZoneTabVehicles();
          fetchStatistics();
        }}
      />
      <BulkZoneDialog open={openBulkDialog} onClose={() => setOpenBulkDialog(false)}
        vehicles={zoneTabVehicles}
        onSuccess={() => { setOpenBulkDialog(false); fetchZoneTabVehicles(); }} />
      <ZoneTemplatesDialog open={openTemplatesDialog} onClose={() => setOpenTemplatesDialog(false)}
        onSelect={() => { setOpenTemplatesDialog(false); setOpenZoneDialog(true); }} />

      <Dialog open={openExportDialog} onClose={() => setOpenExportDialog(false)}
        fullScreen={isMobile} maxWidth="sm" fullWidth>
        <DialogTitle>Export Violations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Export speed and geofence events from the last 7 days:</Typography>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
            <Button variant="outlined" fullWidth onClick={() => { exportViolations('csv'); setOpenExportDialog(false); }}>CSV</Button>
            <Button variant="outlined" fullWidth onClick={() => { exportViolations('json'); setOpenExportDialog(false); }}>JSON</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)} fullWidth={isMobile}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeofencingDashboard;